import type { UserProfile } from "@/schemas/user/profile";

// profile completion requirements constants
export const PUBLIC_PHOTO_REQUIREMENT = {
  MIN_COUNT: 3,
  SCORE_EACH: 10,
} as const;

export const PROFILE_ATTRIBUTE_REQUIREMENT = {
  MIN_COUNT: 8,
  SCORE_EACH: 5,
} as const;

export const ProfileCompletionScore = {
  AVATAR: 25,
  PUBLIC_PHOTOS:
    PUBLIC_PHOTO_REQUIREMENT.MIN_COUNT * PUBLIC_PHOTO_REQUIREMENT.SCORE_EACH,
  PROFILE_ATTRIBUTES_DETAILS:
    PROFILE_ATTRIBUTE_REQUIREMENT.MIN_COUNT *
    PROFILE_ATTRIBUTE_REQUIREMENT.SCORE_EACH,
  BACKGROUND_PHOTO: 5,
} as const;

export const FIRST_STAGE_COMPLETION_SCORE =
  ProfileCompletionScore.AVATAR + ProfileCompletionScore.BACKGROUND_PHOTO;

export const PROFILE_COMPLETION_TOTAL_SCORE = Object.values(
  // stage two
  ProfileCompletionScore,
).reduce((sum, score) => sum + score, 0);

// profile completion check functions
export function isAvatarCompleted(profile: UserProfile): boolean {
  return profile.avatar !== null;
}

export function isBackgroundPhotoCompleted(profile: UserProfile): boolean {
  return profile.background !== null;
}

export function isPublicPhotosRequirementCompleted(
  profile: UserProfile,
): boolean {
  return profile.publicImages.total >= PUBLIC_PHOTO_REQUIREMENT.MIN_COUNT;
}

export function isProfileAttributesRequirementCompleted(
  profile: UserProfile,
): boolean {
  return (
    countFilledProfileAttributes(profile) >=
    PROFILE_ATTRIBUTE_REQUIREMENT.MIN_COUNT
  );
}

// profile completion individual scoring functions
export function getAvatarScore(userProfile: UserProfile): number {
  return isAvatarCompleted(userProfile) ? ProfileCompletionScore.AVATAR : 0;
}

export function getBackgroundPhotoScore(userProfile: UserProfile): number {
  return isBackgroundPhotoCompleted(userProfile)
    ? ProfileCompletionScore.BACKGROUND_PHOTO
    : 0;
}

export function getPublicPhotosScore(userProfile: UserProfile): number {
  const count = userProfile.publicImages.total;
  return (
    Math.min(count, PUBLIC_PHOTO_REQUIREMENT.MIN_COUNT) *
    PUBLIC_PHOTO_REQUIREMENT.SCORE_EACH
  );
}

export function getProfileAttributesScore(userProfile: UserProfile): number {
  const count = countFilledProfileAttributes(userProfile);
  return (
    Math.min(count, PROFILE_ATTRIBUTE_REQUIREMENT.MIN_COUNT) *
    PROFILE_ATTRIBUTE_REQUIREMENT.SCORE_EACH
  );
}

export function countFilledProfileAttributes(userProfile: UserProfile): number {
  return Object.values(userProfile.attributes).filter(
    (value) => value !== null && (!Array.isArray(value) || value.length > 0),
  ).length;
}

// completion scoring functions by stage
export function getFirstStageCompletionScore(userProfile: UserProfile): number {
  const totalScore =
    getAvatarScore(userProfile) + getBackgroundPhotoScore(userProfile);
  return totalScore;
}

export function getUserProfileCompletionScore(
  userProfile: UserProfile,
): number {
  const totalScore =
    getAvatarScore(userProfile) +
    getBackgroundPhotoScore(userProfile) +
    getPublicPhotosScore(userProfile) +
    getProfileAttributesScore(userProfile);
  return totalScore;
}
